/**
 * Connecting this device to a Supabase project: by hand (URL + publishable key)
 * or from an invite (a link or code a colleague shares). An invite holds only
 * public data: the project URL and its publishable key, never a password.
 */
import { linkTarget, saveDeviceServer, server } from './config';

/** The database schema this app needs (`nexus_version()` in supabase/migrations). */
export const SCHEMA_VERSION = 1;

export interface Connection {
  url: string;
  key: string;
  /** Only set when this project shares a Supabase instance with other apps. */
  schema?: string;
  /** The hosted web app, so mails sent from this device (invite, password) lead there. */
  webUrl?: string;
}

const PREFIX = 'nexus-open:';

const b64url = (s: string) =>
  btoa(String.fromCharCode(...new TextEncoder().encode(s)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const unb64url = (s: string) => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
};

/** A code to paste in another copy of Nexus Open. */
export function inviteCode(conn: Connection): string {
  return (
    PREFIX +
    b64url(
      JSON.stringify({
        u: conn.url,
        k: conn.key,
        ...(conn.schema ? { s: conn.schema } : {}),
        ...(conn.webUrl ? { w: conn.webUrl } : {}),
      }),
    )
  );
}

/** A link that opens the web app and offers to connect (`webUrl` empty: the code only). */
export function inviteLink(conn: Connection, webUrl: string): string {
  const code = inviteCode(conn);
  if (!webUrl) return code;
  const u = new URL(webUrl);
  u.searchParams.set('connect', code.slice(PREFIX.length));
  return u.toString();
}

/** Turns a URL like "abc.supabase.co" or "https://abc.supabase.co/" into "https://abc.supabase.co". */
export function normalizeUrl(input: string): string {
  let s = input.trim();
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  const u = new URL(s);
  if (u.protocol !== 'https:' && !['localhost', '127.0.0.1', '[::1]'].includes(u.hostname))
    throw new Error('insecure');
  return u.origin;
}

/** Read an invite: a code, or a link with ?connect=… . Null when it is neither. */
export function parseInvite(input: string): Connection | null {
  let s = input.trim();
  try {
    const fromLink = /^https?:\/\//i.test(s) ? new URL(s).searchParams.get('connect') : null;
    if (fromLink) s = PREFIX + fromLink;
  } catch {
    return null;
  }
  if (!s.startsWith(PREFIX)) return null;
  try {
    const {
      u,
      k,
      s: schema,
      w,
    } = JSON.parse(unb64url(s.slice(PREFIX.length))) as {
      u?: unknown;
      k?: unknown;
      s?: unknown;
      w?: unknown;
    };
    if (typeof u !== 'string' || typeof k !== 'string' || !k) return null;
    return {
      url: normalizeUrl(u),
      key: k,
      ...(typeof schema === 'string' && schema ? { schema } : {}),
      ...(typeof w === 'string' && /^https?:\/\//i.test(w) ? { webUrl: w } : {}),
    };
  } catch {
    return null;
  }
}

export type CheckResult =
  | { ok: true; version: number }
  | { ok: false; reason: 'unreachable' | 'key' | 'no-schema' | 'old-schema'; version?: number };

/** Is this a Nexus Open server, with a schema this app can use? */
export async function checkServer(conn: Connection): Promise<CheckResult> {
  let res: Response;
  try {
    res = await fetch(`${conn.url}/rest/v1/rpc/nexus_version`, {
      method: 'POST',
      headers: {
        apikey: conn.key,
        'Content-Type': 'application/json',
        ...(conn.schema ? { 'Content-Profile': conn.schema, 'Accept-Profile': conn.schema } : {}),
      },
      body: '{}',
    });
  } catch {
    return { ok: false, reason: 'unreachable' };
  }
  if (res.status === 401 || res.status === 403) return { ok: false, reason: 'key' };
  if (!res.ok) return { ok: false, reason: 'no-schema' };
  const version = Number(await res.json().catch(() => NaN));
  if (!Number.isFinite(version)) return { ok: false, reason: 'no-schema' };
  if (version < SCHEMA_VERSION) return { ok: false, reason: 'old-schema', version };
  return { ok: true, version };
}

/** Keep the connection on this device and start again with it. */
export function connect(conn: Connection): void {
  saveDeviceServer(conn);
  window.location.replace(window.location.pathname);
}

/** Forget the connection made on this device (sign out first). */
export function disconnect(): void {
  saveDeviceServer(null);
  window.location.replace(window.location.pathname);
}

/** The connection others can join with, if this device has one. */
export function currentConnection(): Connection | null {
  const s = server();
  const webUrl = linkTarget();
  return s.url && s.key
    ? {
        url: s.url,
        key: s.key,
        ...(s.schema !== 'public' ? { schema: s.schema } : {}),
        ...(webUrl ? { webUrl } : {}),
      }
    : null;
}

/** An invite in this page's address (`?connect=…`), read once at start. */
export function inviteInAddress(): Connection | null {
  if (typeof window === 'undefined') return null;
  const code = new URLSearchParams(window.location.search).get('connect');
  return code ? parseInvite(PREFIX + code) : null;
}
